// client/src/contexts/AuthContext.jsx
import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE_URL } from '../config';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isWarningActive, setIsWarningActive] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(120); // 2 minutes warning
  const lastActivityRef = useRef(Date.now());
  const timerRef = useRef(null);

  const [idleTimeoutMinutes, setIdleTimeoutMinutes] = useState(() => {
    return parseInt(localStorage.getItem('idleTimeoutMinutes')) || 20;
  });

  const IDLE_THRESHOLD = (idleTimeoutMinutes - 2) * 60 * 1000;
  const WARNING_DURATION = 2 * 60 * 1000;
  const TOTAL_TIMEOUT = idleTimeoutMinutes * 60 * 1000;

  const resetIdleTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    if (isWarningActive) {
      setIsWarningActive(false);
      setSecondsRemaining(120);
    }
  }, [isWarningActive]);

  const updateIdleTimeout = (minutes) => {
    setIdleTimeoutMinutes(minutes);
    localStorage.setItem('idleTimeoutMinutes', minutes.toString());
  };

  const getAuthHeaders = useCallback((token) => {
    const headers = { 'Content-Type': 'application/json' };
    
    // Check for connection-level credentials (Basic Auth)
    const username = localStorage.getItem('server_username');
    const password = localStorage.getItem('server_password');
    if (username && password) {
      try {
        const auth = btoa(`${username}:${password}`);
        headers['Authorization'] = `Basic ${auth}`;
      } catch (e) {}
    }

    // If we have an application token (JWT), it overrides Basic Auth for API logic
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    return headers;
  }, []);

  const logout = useCallback(() => {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('serverInstanceId');
    setUser(null);
    setIsWarningActive(false);
  }, []);

  // Handle explicit signout on closure (additional safety)
  useEffect(() => {
    if (!user) return;

    const handleUnload = () => {
      // Clear token on complete closure if necessary
      // Since we use sessionStorage, it's already handled, 
      // but explicit logout call can be added here for server-side cleanups if needed.
    };

    window.addEventListener('unload', handleUnload);
    return () => window.removeEventListener('unload', handleUnload);
  }, [user]);

  useEffect(() => {
    if (!user) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }

    // RESET TIMER ONCE WHEN USER LOGS IN OR SESSION RESUMES
    lastActivityRef.current = Date.now();
    setIsWarningActive(false);
    setSecondsRemaining(120);

    const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];
    
    activityEvents.forEach(event => {
      window.addEventListener(event, resetIdleTimer);
    });

    timerRef.current = setInterval(() => {
      const now = Date.now();
      const idleTime = now - lastActivityRef.current;

      if (idleTime >= TOTAL_TIMEOUT) {
        logout();
      } else if (idleTime >= IDLE_THRESHOLD) {
        if (!isWarningActive) setIsWarningActive(true);
        const remaining = Math.max(0, Math.ceil((TOTAL_TIMEOUT - idleTime) / 1000));
        setSecondsRemaining(remaining);
      } else {
        if (isWarningActive) setIsWarningActive(false);
      }
    }, 1000);

    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, resetIdleTimer);
      });
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [user, resetIdleTimer, logout, IDLE_THRESHOLD, TOTAL_TIMEOUT]);

  useEffect(() => {
    // Check if user is logged in on initial load
    const checkAuth = async () => {
      try {
        const token = sessionStorage.getItem('token');
        if (!token) {
          setLoading(false);
          return;
        }

        const response = await fetch(`${API_BASE_URL}/auth/me`, {
          headers: getAuthHeaders(token)
        });

        if (response.ok) {
          const data = await response.json();
          setUser(data.user);
          
          // Clinic Hub Identity Check
          const currentInstanceId = sessionStorage.getItem('serverInstanceId');
          if (data.serverInstanceId) {
            if (currentInstanceId && currentInstanceId !== data.serverInstanceId) {
              console.warn('[Auth] Server restart detected during clinical check. Session invalidated.');
              logout();
            } else {
              sessionStorage.setItem('serverInstanceId', data.serverInstanceId);
            }
          }
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        sessionStorage.removeItem('token');
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (identifier, password) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ identifier, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      // If 2FA is required, return the partial data to the Login component
      if (data.require_2fa) {
        return { success: true, require_2fa: true, userId: data.userId, email_hint: data.email_hint };
      }

      sessionStorage.setItem('token', data.token);
      if (data.serverInstanceId) {
        sessionStorage.setItem('serverInstanceId', data.serverInstanceId);
      }
      setUser(data.user);
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message };
    }
  };

  const verify2FA = async (userId, code) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/verify-2fa`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ userId, code }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Verification failed');
      }

      sessionStorage.setItem('token', data.token);
      if (data.serverInstanceId) {
        sessionStorage.setItem('serverInstanceId', data.serverInstanceId);
      }
      setUser(data.user);
      return { success: true };
    } catch (error) {
      console.error('2FA verification error:', error);
      return { success: false, error: error.message };
    }
  };

  const value = {
    user,
    isAuthenticated: !!user,
    login,
    verify2FA,
    logout,
    signOut: logout, // alias used by MainLayout
    loading,
    isWarningActive,
    secondsRemaining,
    resetIdleTimer,
    idleTimeoutMinutes,
    updateIdleTimeout
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#0f1117' }}>
          <div style={{ textAlign: 'center', color: '#fff' }}>
            <h2 style={{ color: '#2c7be5', fontFamily: 'Inter, sans-serif', fontSize: '1.8rem', marginBottom: 4 }}>
              Bolanis Ioannis MyEMR <span style={{ fontSize: '1rem', color: '#667', fontWeight: 500 }}>v1.0.0</span>
            </h2>
            <p style={{ color: '#aaa', fontFamily: 'Inter, sans-serif' }}>Loading application...</p>
          </div>
        </div>
      ) : children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};